import { css } from '@emotion/react';
import { Box, Text, Title } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

const REQUIREMENT_ROWS = [
  {
    labelKey: 'ai_interviews.landing.requirements.device.label',
    labelDefault: '기기',
    valueKey: 'ai_interviews.landing.requirements.device.value',
    valueDefault: 'PC에서만 이용할 수 있어요.',
  },
  {
    labelKey: 'ai_interviews.landing.requirements.hardware.label',
    labelDefault: '장치',
    valueKey: 'ai_interviews.landing.requirements.hardware.value',
    valueDefault:
      '마이크·스피커가 되는 환경에서만 응시할 수 있어요. 마이크는 답변 기록에 꼭 필요해요.',
  },
  {
    labelKey: 'ai_interviews.landing.requirements.browser.label',
    labelDefault: '브라우저',
    valueKey: 'ai_interviews.landing.requirements.browser.value',
    valueDefault: '크롬·사파리 브라우저에서 이용할 수 있어요.',
  },
  {
    labelKey: 'ai_interviews.landing.requirements.job.label',
    labelDefault: '지원 직무',
    valueKey: 'ai_interviews.landing.requirements.job.value',
    valueDefault: '지금은 백엔드 직무만 지원해요. 다른 직무도 순차적으로 추가될 예정이에요.',
  },
  {
    labelKey: 'ai_interviews.landing.requirements.docs.label',
    labelDefault: '필요 서류',
    valueKey: 'ai_interviews.landing.requirements.docs.value',
    valueDefault: '이력서·포트폴리오 파일이 필요해요. 질문은 올려주신 파일을 바탕으로 만들어져요.',
  },
] as const;

/** 시작 전 확인 — PreStartNotice 대체 (스펙 Requirements) */
export const Requirements = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-req-title">
      <Box css={styles.inner}>
        <Title order={3} id="ai-req-title" css={styles.title}>
          {t('ai_interviews.landing.requirements.title', '시작 전 꼭 확인해주세요')}
        </Title>
        <Text css={styles.subtitle}>
          {t(
            'ai_interviews.landing.requirements.subtitle',
            'AI 모의면접은 높은 퀄리티로 제공하기 위해 아래 환경이 필요해요.',
          )}
        </Text>

        <Box css={styles.list}>
          {REQUIREMENT_ROWS.map((row, index) => (
            <Box
              key={row.labelKey}
              css={[styles.row, index === REQUIREMENT_ROWS.length - 1 ? styles.rowLast : null]}
            >
              <Text css={styles.label}>{t(row.labelKey, row.labelDefault)}</Text>
              <Text css={styles.value}>{t(row.valueKey, row.valueDefault)}</Text>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    background: '#fff',
    padding: `${LANDING_SPACE.sectionTop} ${LANDING_SPACE.pageX} 0`,
  }),
  inner: css({
    maxWidth: 960,
    margin: '0 auto',
  }),
  title: css({
    margin: 0,
    fontSize: LANDING_FONT.sectionTitleSm,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: LANDING_COLOR.ink,
    wordBreak: 'keep-all',
  }),
  subtitle: css({
    marginTop: LANDING_SPACE.stackGap,
    marginBottom: LANDING_SPACE.subtitleBottom,
    color: LANDING_COLOR.bodyMuted,
    fontSize: LANDING_FONT.bodyMd,
    lineHeight: 1.75,
    textWrap: 'pretty',
    wordBreak: 'keep-all',
  }),
  list: css({
    borderTop: 'none',
  }),
  row: css({
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 300px) 1fr',
    gap: LANDING_SPACE.rowGap,
    padding: `${LANDING_SPACE.rowY} 0`,
    borderTop: '1px solid rgba(17,19,21,.1)',
    [LANDING_MOBILE_MQ]: {
      gridTemplateColumns: '1fr',
    },
  }),
  rowLast: css({
    borderBottom: '1px solid rgba(17,19,21,.1)',
  }),
  label: css({
    fontSize: LANDING_FONT.body,
    fontWeight: 600,
    color: LANDING_COLOR.ink,
  }),
  value: css({
    fontSize: LANDING_FONT.body,
    lineHeight: 1.75,
    color: LANDING_COLOR.bodyMuted,
    wordBreak: 'keep-all',
  }),
};
